// "Recent ▾" header menu: the customer's most recent distinct configurations, each loaded whole
// in one click as the prediction (grey, like any prediction).
//
// Kevin, 2026-10-17: yes to "pick a recent config". A repeat order is right on ~99% of answers
// once the right earlier configuration is found, and the customer's 5 most recent distinct ones
// hold it 59-67% of the time (scratch/predict/research/accuracy). New Config only: Edit Config
// restores the item's own configuration.

import { useEffect, useState } from "react";
import { useAnswers } from "../state/AnswersContext";
import { getLaunchContext } from "../util/launchContext";
import { fetchRecentConfigs, toInput, toSource, type RecentConfig } from "../util/predictionClient";

// Launch ids carry a prefix for the outcome table (R = picked here, S = session launch); the
// row underneath is the same.
const rowId = (id: string) => id.replace(/^[RS]/, "");
const dateOf = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(d);
};

export default function RecentConfigs() {
  const { product, configMode, prediction, applyPrediction } = useAnswers();
  const [items, setItems] = useState<RecentConfig[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ctx = getLaunchContext();
    if (ctx.itemId || !ctx.customerId) return;
    let alive = true;
    void fetchRecentConfigs(product, ctx.customerId).then((list) => {
      if (alive) setItems(list);
    });
    return () => {
      alive = false;
    };
  }, [product]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (configMode !== "new" || items.length === 0) return null;
  const current = prediction ? rowId(prediction.launchSource.id) : null;

  const pick = async (it: RecentConfig) => {
    setOpen(false);
    setBusy(true);
    try {
      const source = { ...toSource({ ...it, found: true, basis: "customer" }), id: `R${it.source!.id}` };
      await applyPrediction(toInput({ ...it, found: true }), source, null, { replace: true, held: true });
    } catch (e) {
      console.warn("[cpq2] recent configuration not applied", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cpq-audit-wrap">
      <button
        type="button"
        className="cpq-audit-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
      >
        {busy ? "Loading…" : "Recent"} <span aria-hidden>▾</span>
      </button>

      {open && (
        <>
          <div className="cpq-audit-backdrop" onClick={() => setOpen(false)} />
          <ul className="cpq-audit-menu cpq-recent-menu" role="menu" aria-label="Recent configurations">
            {items.map((it) => {
              const id = it.source!.id;
              const isCurrent = current === rowId(id);
              return (
                <li role="none" key={id}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={isCurrent}
                    className={`cpq-audit-menu-item cpq-recent-item${isCurrent ? " is-current" : ""}`}
                    title={it.source!.name}
                    onClick={() => void pick(it)}
                  >
                    <span className="cpq-recent-name">{it.source!.name}</span>
                    <span className="cpq-recent-date">{dateOf(it.source!.createdAt)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
